// client/src/components/ScheduleOverlaps.js
import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ApiLeagues from '../../api/ApiLeagues'
import PageLayout from '../common/layout/PageLayout'
import MatchProposalModal from './MatchProposalModal'
import toastr from 'toastr'
import { format, parseISO } from 'date-fns'
import { services } from '../../services'
import './ScheduleOverlaps.css'

function ScheduleOverlaps() {
  const [overlaps, setOverlaps] = useState([])
  const [leagues, setLeagues] = useState([])
  const [selectedLeagueId, setSelectedLeagueId] = useState('')
  const [loading, setLoading] = useState(false)
  const [leaguesLoading, setLeaguesLoading] = useState(true)

  // Data states
  const [matchProposals, setMatchProposals] = useState([])
  const [scheduledMatches, setScheduledMatches] = useState([])

  // Match proposal modal state
  const [proposalModalOpen, setProposalModalOpen] = useState(false)
  const [selectedOpponent, setSelectedOpponent] = useState(null)
  const [proposedTime, setProposedTime] = useState(null)
  const [availabilityStart, setAvailabilityStart] = useState(null)
  const [availabilityEnd, setAvailabilityEnd] = useState(null)

  const navigate = useNavigate()
  const { leagueId } = useParams() // Get league ID from URL

  const fetchLeagues = useCallback(async () => {
    try {
      setLeaguesLoading(true)
      const data = await ApiLeagues.getMyLeagues()
      setLeagues(data)

      // Prioritize league ID from URL, then auto-select first active league
      if (leagueId) {
        // Check if the league ID from URL is valid and user has access to it
        const urlLeague = data.find(
          (league) => league.league_id.toString() === leagueId,
        )
        if (urlLeague) {
          setSelectedLeagueId(leagueId)
          return
        }
      }

      // Fallback: auto-select first active league if available
      const activeLeague = data.find((league) =>
        ['active', 'in_progress', 'playoffs'].includes(league.league_status),
      )
      if (activeLeague) {
        setSelectedLeagueId(activeLeague.league_id.toString())
      }
    } catch (err) {
      console.error('Failed to load leagues:', err)
      toastr.error('Failed to load leagues')
    } finally {
      setLeaguesLoading(false)
    }
  }, [leagueId])

  useEffect(() => {
    fetchLeagues()
  }, [fetchLeagues]) // Now include fetchLeagues in the dependency array

  const fetchOverlaps = useCallback(async () => {
    if (!selectedLeagueId) return

    try {
      setLoading(true)

      // Load overlaps
      const response =
        await services.availability.getAvailabilityOverlaps(selectedLeagueId)
      if (response.success) {
        setOverlaps(response.data.overlaps || [])
      }

      // Load match proposals with league context
      try {
        // First get division info to get division_id
        const leagueResponse =
          await services.league.getMyDivision(selectedLeagueId)

        let proposalsResponse
        if (leagueResponse.success && leagueResponse.data.division_id) {
          proposalsResponse = await services.matchProposal.getReceivedProposals(
            parseInt(selectedLeagueId),
            leagueResponse.data.division_id,
          )
        } else {
          proposalsResponse =
            await services.matchProposal.getReceivedProposals()
        }

        if (proposalsResponse.success) {
          setMatchProposals(proposalsResponse.data || [])
        }

        // Load scheduled matches for this league
        if (leagueResponse.success && leagueResponse.data.matches) {
          const scheduled = leagueResponse.data.matches.filter(
            (match) => match.status === 'scheduled' && match.match_datetime,
          )
          setScheduledMatches(scheduled)
        }
      } catch (err) {
        console.error('Failed to load scheduled matches:', err)
      }
    } catch (err) {
      console.error('Failed to load overlaps:', err)
    } finally {
      setLoading(false)
    }
  }, [selectedLeagueId])

  useEffect(() => {
    fetchOverlaps()
  }, [fetchOverlaps])

  const formatTimeRange = (startTime, endTime) => {
    try {
      const start = format(parseISO(startTime), 'h:mm a')
      const end = format(parseISO(endTime), 'h:mm a')
      return `${start} - ${end}`
    } catch {
      return `${startTime} - ${endTime}`
    }
  }

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'EEEE, MMM d, yyyy')
    } catch {
      return dateString
    }
  }

  const groupOverlapsByDate = (overlaps) => {
    const grouped = {}

    overlaps.forEach((overlap) => {
      const date = parseISO(overlap.start_time)
      const dateKey = format(date, 'yyyy-MM-dd')

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          displayDate: formatDate(overlap.start_time),
          overlaps: [],
        }
      }

      grouped[dateKey].overlaps.push(overlap)
    })

    // Sort overlaps within each date by time
    Object.values(grouped).forEach((group) => {
      group.overlaps.sort(
        (a, b) => new Date(a.start_time) - new Date(b.start_time),
      )
    })

    // Convert to array and sort by date
    return Object.values(grouped).sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    )
  }

  const handleProposeMatch = (opponent, startTime, endTime) => {
    if (!selectedLeagueId) {
      toastr.error('Please select a league first')
      return
    }

    // Open the match proposal modal
    setSelectedOpponent(opponent)
    setProposedTime(startTime)
    setAvailabilityStart(startTime)
    setAvailabilityEnd(endTime)
    setProposalModalOpen(true)
  }

  // Match proposal modal handlers
  const handleMatchProposalConfirm = async (
    opponentPlayerId,
    proposedTime,
    leagueId,
    expiresAt,
    notes,
    directProposal,
  ) => {
    try {
      const result = await services.matchProposal.createProposal(
        opponentPlayerId,
        proposedTime,
        leagueId,
        undefined, // divisionId - not needed for schedule overlaps
        expiresAt,
        {
          notes,
          directProposal,
        },
      )

      if (result.success) {
        // Optionally refresh overlaps to show any updates
        if (selectedLeagueId) {
          await fetchOverlaps()
        }
      }
    } catch (error) {
      console.error('Error creating match proposal:', error)
      toastr.error('Failed to send match proposal')
    }
  }

  const closeProposalModal = () => {
    setProposalModalOpen(false)
    setSelectedOpponent(null)
    setProposedTime(null)
    setAvailabilityStart(null)
    setAvailabilityEnd(null)
  }

  const getActiveLeagues = () => {
    return leagues.filter((league) =>
      ['active', 'in_progress', 'playoffs'].includes(league.league_status),
    )
  }

  // Helper functions for match proposals and scheduled matches
  const getMatchProposalForOpponent = (opponentId, startTime) => {
    const proposedTime = new Date(startTime)

    return matchProposals.find((proposal) => {
      if (!proposal.proposed_time) return false

      const proposalTime = new Date(proposal.proposed_time)
      const timeDiff = Math.abs(proposalTime.getTime() - proposedTime.getTime())

      // Check if this proposal involves the opponent and time is within 1 hour
      const isOpponentMatch =
        proposal.opponent_player_id === opponentId ||
        proposal.proposer_player_id === opponentId

      return timeDiff < 3600000 && isOpponentMatch
    })
  }

  const getScheduledMatchForOpponent = (opponentId, startTime) => {
    const proposedTime = new Date(startTime)

    return scheduledMatches.find((match) => {
      if (!match.match_datetime) return false

      const matchTime = new Date(match.match_datetime)
      const timeDiff = Math.abs(matchTime.getTime() - proposedTime.getTime())

      // Check if this match involves the opponent
      const isOpponentMatch =
        match.home_player_id === opponentId ||
        match.away_player_id === opponentId

      return timeDiff < 3600000 && isOpponentMatch
    })
  }

  if (leaguesLoading) {
    return (
      <PageLayout title="Schedule Overlaps">
        <div className="content-card">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading leagues...
          </div>
        </div>
      </PageLayout>
    )
  }

  const activeLeagues = getActiveLeagues()

  if (activeLeagues.length === 0) {
    return (
      <PageLayout title="Schedule Overlaps">
        <div className="content-card">
          <div className="no-leagues-message">
            <h3>No Active Leagues</h3>
            <p>You need to be in an active league to view schedule overlaps.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/my-leagues')}
              style={{ marginTop: '1rem' }}
            >
              View My Leagues
            </button>
          </div>
        </div>
      </PageLayout>
    )
  }

  const groupedOverlaps = groupOverlapsByDate(overlaps)

  return (
    <PageLayout title="Schedule Overlaps">
      <div className="content-card">
        <div className="overlaps-header">
          <div className="league-selector">
            <label htmlFor="league-select">League:</label>
            <select
              id="league-select"
              value={selectedLeagueId}
              onChange={(e) => setSelectedLeagueId(e.target.value)}
              className="form-select"
            >
              <option value="">Select a league...</option>
              {activeLeagues.map((league) => (
                <option key={league.league_id} value={league.league_id}>
                  {league.league_name}
                </option>
              ))}
            </select>
          </div>

          <button
            className="btn btn-secondary"
            onClick={() =>
              selectedLeagueId
                ? navigate(`/leagues/${selectedLeagueId}/availability`)
                : navigate('/availability')
            }
          >
            Manage Availability
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading overlaps...
          </div>
        ) : !selectedLeagueId ? (
          <div className="no-selection-message">
            <p>
              Select a league above to view schedule overlaps with your
              opponents.
            </p>
          </div>
        ) : groupedOverlaps.length > 0 ? (
          <div className="overlaps-list">
            {groupedOverlaps.map((group) => (
              <div key={group.date} className="date-group">
                <div className="date-header">
                  <h3>{group.displayDate}</h3>
                </div>

                <div className="time-slots">
                  {group.overlaps.map((overlap, index) => (
                    <div key={index} className="overlap-item">
                      <div className="time-info">
                        <div className="time-range">
                          {formatTimeRange(
                            overlap.start_time,
                            overlap.end_time,
                          )}
                        </div>
                      </div>

                      <div className="opponents-section">
                        <div className="opponents-label">
                          Available Opponents (
                          {overlap.available_opponents.length}):
                        </div>
                        <div className="opponents-list">
                          {overlap.available_opponents.map((opponent) => {
                            // Check for existing match proposal or scheduled match
                            const existingProposal =
                              getMatchProposalForOpponent(
                                opponent.id,
                                overlap.start_time,
                              )
                            const scheduledMatch = getScheduledMatchForOpponent(
                              opponent.id,
                              overlap.start_time,
                            )

                            return (
                              <div key={opponent.id} className="opponent-item">
                                <div className="opponent-name">
                                  {opponent.first_name} {opponent.last_name}
                                </div>
                                {existingProposal ? (
                                  <div className="proposal-info">
                                    Proposal sent{' '}
                                    {format(
                                      new Date(existingProposal.proposed_time),
                                      'h:mm a',
                                    )}
                                  </div>
                                ) : scheduledMatch ? (
                                  <div className="match-info">
                                    Scheduled Match{' '}
                                    {format(
                                      new Date(scheduledMatch.match_datetime),
                                      'h:mm a',
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() =>
                                      handleProposeMatch(
                                        opponent,
                                        overlap.start_time,
                                        overlap.end_time,
                                      )
                                    }
                                  >
                                    Propose Match
                                  </button>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-overlaps-message">
            <h3>No Schedule Overlaps Found</h3>
            <p>
              There are currently no times when you and your unplayed opponents
              are both available. Try adding more availability or check back
              later.
            </p>
            <button
              className="btn btn-primary"
              onClick={() =>
                navigate(
                  selectedLeagueId
                    ? `/leagues/${selectedLeagueId}/availability/add`
                    : '/availability/add',
                )
              }
              style={{ marginTop: '1rem' }}
            >
              Add More Availability
            </button>
          </div>
        )}

        {/* Match Proposal Modal */}
        <MatchProposalModal
          isOpen={proposalModalOpen}
          onClose={closeProposalModal}
          opponent={selectedOpponent}
          proposedTime={proposedTime}
          availabilityStart={availabilityStart}
          availabilityEnd={availabilityEnd}
          leagueId={selectedLeagueId}
          onConfirm={handleMatchProposalConfirm}
        />
      </div>
    </PageLayout>
  )
}

export default ScheduleOverlaps
